import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ApplicationTracker = ({ applications }) => {
  const [activeTab, setActiveTab] = useState('all');

  const tabs = [
    { id: 'all', label: 'All', count: applications?.length },
    { id: 'pending', label: 'Pending', count: applications?.filter(app => app?.status === 'pending')?.length },
    { id: 'accepted', label: 'Accepted', count: applications?.filter(app => app?.status === 'accepted')?.length },
    { id: 'rejected', label: 'Rejected', count: applications?.filter(app => app?.status === 'rejected')?.length }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-success/10 text-success border-success/20';
      case 'rejected':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-warning/10 text-warning border-warning/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
        return 'CheckCircle';
      case 'rejected':
        return 'XCircle';
      default:
        return 'Clock';
    }
  };

  const filteredApplications = activeTab === 'all' 
    ? applications 
    : applications?.filter(app => app?.status === activeTab);

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-PH', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-md overflow-hidden">
      <div className="p-5 border-b border-border">
        <h2 className="text-lg font-heading font-semibold text-foreground flex items-center gap-2">
          <Icon name="FileText" size={20} color="var(--color-primary)" />
          My Applications
        </h2>
      </div>
      <div className="flex border-b border-border overflow-x-auto">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`
              flex items-center gap-2 px-4 py-3 text-sm font-medium
              transition-smooth border-b-2 whitespace-nowrap flex-shrink-0
              ${activeTab === tab?.id
                ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }
            `}
          >
            <span>{tab?.label}</span>
            <span className={`
              px-2 py-0.5 rounded-full text-xs font-semibold
              ${activeTab === tab?.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
            `}>
              {tab?.count}
            </span>
          </button>
        ))}
      </div>
      <div className="max-h-[600px] overflow-y-auto">
        {filteredApplications?.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Inbox" size={32} color="var(--color-muted-foreground)" />
            </div>
            <p className="text-sm text-muted-foreground">
              {activeTab === 'all' ?'No applications yet. Start applying to jobs!' 
                : `No ${activeTab} applications`}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredApplications?.map((application) => (
              <div key={application?.id} className="p-4 hover:bg-muted/50 transition-smooth">
                <div className="flex items-start gap-3 mb-3">
                  <Image
                    src={application?.jobImage}
                    alt={application?.jobImageAlt}
                    className="w-10 h-10 rounded-md object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground line-clamp-1 mb-1">
                      {application?.jobTitle}
                    </h3>
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {application?.clientName}
                    </p>
                  </div>
                  <span className={`
                    px-2 py-1 rounded-md text-xs font-medium border flex-shrink-0
                    ${getStatusColor(application?.status)}
                  `}>
                    <Icon name={getStatusIcon(application?.status)} size={12} className="inline mr-1" />
                    {application?.status?.charAt(0)?.toUpperCase() + application?.status?.slice(1)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Icon name="Wallet" size={12} />
                    <span>₱{application?.proposedRate?.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Icon name="Calendar" size={12} />
                    <span>{formatDate(application?.appliedDate)}</span>
                  </div>
                </div>

                {application?.clientMessage && (
                  <div className="mt-3 p-2 bg-primary/5 rounded-md">
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      <Icon name="MessageSquare" size={12} className="inline mr-1" />
                      {application?.clientMessage}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationTracker;